import { useState } from "react";
import api, { apiError, brl, fileUrl, printAgent } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Undo2, FileText, PenLine, Check, Printer, KeyRound } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import CustomerPicker, { FREE_TEXT } from "@/components/panel/pickers/CustomerPicker";
import ProductPicker, { FREE_TEXT as PRODUCT_FREE_TEXT } from "@/components/panel/pickers/ProductPicker";
import { Badge, Button, DataTable, Field, Input, PageHeader, SearchInput } from "@/components/ui-kit";

// Imprime o contrato de locação (PDF do backend) via agente local.
async function printContract(id) {
  const tid = toast.loading("Enviando contrato para a impressora...");
  try {
    await printAgent.printApiPdf(`/admin/rentals/${id}/contract`);
    toast.success("Contrato enviado para impressão", { id: tid });
  } catch (e) {
    toast.error(e.message || "Falha ao imprimir", { id: tid });
  }
}

/* Tom semântico do Badge: em curso = informação, devolvido = concluído, atrasado = falha. */
const RENTAL_TONE = { ativo: "info", devolvido: "success", atrasado: "danger" };

const dueBadge = (r) => {
  if (r.status !== "ativo") return null;
  const days = Math.ceil((new Date(r.due_date) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return <span className="ml-1.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">vence hoje</span>;
  if (days === 1) return <span className="ml-1.5 text-[10px] font-bold uppercase bg-amber-50 text-amber-600 rounded px-1.5 py-0.5">vence amanhã</span>;
  return null;
};

export default function Rentals() {
  const [showForm, setShowForm] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ customer_id: null, product_id: null, variant_id: null, customer_name: "", customer_document: "", phone: "", equipment: "", daily_rate: 15, deposit: 200, entry_amount: 0, installments: 1, installment_value: 0, start_date: today, due_date: today, notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [busy, setBusy] = useState(false);
  const [returning, setReturning] = useState(null);

  // Lista paginada no servidor; a busca também vai para o banco, senão só
  // encontraria contratos dentro da página aberta.
  const {
    items: rentals, total, page, lastPage, setPage, search, setSearch,
    isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/admin/rentals");

  const create = async () => {
    if (!form.customer_name || !form.equipment) { toast.error("Preencha cliente e equipamento"); return; }
    setBusy(true);
    try {
      await api.post("/admin/rentals", {
        ...form,
        daily_rate: parseFloat(form.daily_rate), deposit: parseFloat(form.deposit),
        entry_amount: parseFloat(form.entry_amount) || 0, installments: parseInt(form.installments) || 1, installment_value: parseFloat(form.installment_value) || 0,
      });
      toast.success("Contrato criado"); setShowForm(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doSign = async (r) => {
    setBusy(true);
    try { await api.post(`/admin/rentals/${r.id}/sign`); toast.success("Contrato marcado como assinado/retirado"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doReturn = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/rentals/${r.id}/return`);
      toast.success(`Devolvido: ${data.days} diária(s) = ${brl(data.total)}. Caução ${brl(r.deposit)} a devolver.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-rentals-page">
      <PageHeader
        eyebrow="Operação"
        title="Aluguel de Equipamentos"
        icon={KeyRound}
        description="Contratos com caução. A devolução cobra as diárias corridas e libera o valor retido."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar contrato, cliente ou equipamento" data-testid="rental-search-input" aria-label="Buscar contrato por número, cliente ou equipamento" width="w-72" />
            <Button icon={Plus} onClick={() => setShowForm(true)} data-testid="rental-new-btn">Novo contrato</Button>
          </>
        )}
      />

      <DataTable
        caption="Contratos de aluguel"
        rows={rentals}
        rowKey={(r) => r.id}
        loading={loading}
        error={error}
        onRetry={load}
        empty={{
          icon: KeyRound,
          title: search ? `Nenhum contrato para “${search}”` : "Nenhum contrato de aluguel",
          desc: search
            ? "Tente outro número, cliente ou equipamento."
            : "Cadeiras, andadores e camas hospitalares saem daqui, com caução e prazo de devolução.",
          action: !search && <Button icon={Plus} onClick={() => setShowForm(true)}>Criar contrato</Button>,
          testId: "rental-empty",
        }}
        columns={[
          { key: "contrato", header: "Contrato", mono: true, className: "font-semibold", cell: (r) => r.number },
          {
            key: "cliente", header: "Cliente",
            cell: (r) => (<><p className="text-slate-900">{r.customer_name}</p><p className="text-xs text-slate-400">{r.phone}</p></>),
          },
          { key: "equipamento", header: "Equipamento", className: "text-slate-600", cell: (r) => r.equipment },
          { key: "diaria", header: "Diária", align: "right", mono: true, cell: (r) => brl(r.daily_rate) },
          { key: "caucao", header: "Caução", align: "right", mono: true, cell: (r) => brl(r.deposit) },
          {
            key: "vencimento", header: "Vencimento", className: "text-slate-500",
            cell: (r) => (<>{new Date(r.due_date + "T12:00:00").toLocaleDateString("pt-BR")}{dueBadge(r)}</>),
          },
          {
            key: "status", header: "Status",
            cell: (r) => (
              <>
                <Badge tone={RENTAL_TONE[r.status] || "neutral"}>{r.status}</Badge>
                {r.total_charged != null && <span className="text-xs text-slate-400 ml-1 font-mono">{brl(r.total_charged)}</span>}
              </>
            ),
          },
          {
            key: "acoes", header: "Ações", align: "right", className: "whitespace-nowrap",
            cell: (r) => (
              // Uma ação principal com rótulo (Devolução) e três utilidades em
              // ícone. Com os quatro escritos por extenso a linha estourava a
              // largura da tabela e o botão principal ficava cortado.
              <div className="inline-flex items-center gap-1">
                <a href={fileUrl(`/admin/rentals/${r.id}/contract`)} target="_blank" rel="noreferrer" data-testid="rental-contract-btn"
                  aria-label={`Abrir contrato ${r.number} em PDF`} title="Abrir contrato em PDF"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-brand-600 hover:bg-brand-50"><FileText size={15} /></a>
                <Button variant="ghost" size="iconSm" onClick={() => printContract(r.id)}
                  data-testid="rental-print-btn" aria-label={`Imprimir contrato ${r.number}`} title="Imprimir contrato"><Printer size={15} /></Button>
                {r.signed_at
                  ? <span className="inline-flex items-center justify-center h-8 w-8 text-emerald-600" title={`Contrato ${r.number} assinado`}>
                      <Check size={15} aria-hidden /><span className="sr-only">Assinado</span>
                    </span>
                  : <Button variant="ghost" size="iconSm" onClick={() => doSign(r)} disabled={busy}
                      data-testid="rental-sign-btn" aria-label={`Registrar assinatura do contrato ${r.number}`} title="Registrar assinatura"><PenLine size={15} /></Button>}
                {r.status !== "devolvido" && (
                  <Button variant="navy" size="sm" icon={Undo2} onClick={() => setReturning(r)} disabled={busy}
                    data-testid="rental-return-btn" className="ml-1">Devolução</Button>
                )}
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="contratos" />

      <ConfirmDialog open={!!returning} onClose={() => setReturning(null)} onConfirm={() => doReturn(returning)} danger={false}
        title="Registrar devolução"
        message={returning ? `Confirmar a devolução do contrato ${returning.number}? As diárias serão cobradas e a caução (${brl(returning.deposit)}) liberada. Esta ação encerra o contrato.` : ""}
        confirmLabel="Registrar devolução" />

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Novo contrato de aluguel" maxWidth="max-w-md" testId="rental-form-dialog">
        <div className="space-y-3">
            <Field label="Cliente" required>
                {/* Cadastro preenche documento e telefone — os dois vão para o
                    contrato, e redigitar era a maior fonte de erro. */}
                <CustomerPicker
                  value={form.customer_id || (form.customer_name ? FREE_TEXT : "")}
                  selectedLabel={form.customer_name}
                  onChange={(c) => setForm({
                    ...form,
                    customer_id: c?.id || null,
                    customer_name: c?.name || "",
                    customer_document: c?.document || form.customer_document,
                    phone: c?.phone || form.phone,
                  })}
                  placeholder="Busque um cliente cadastrado ou digite o nome"
                  allowFreeText
                  testId="rental-form-customer"
                />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="CPF/CNPJ" hint="Vai impresso no contrato">
                <Input value={form.customer_document} onChange={set("customer_document")} data-testid="rental-form-document" mono />
              </Field>
              <Field label="Telefone/WhatsApp">
                <Input value={form.phone} onChange={set("phone")} data-testid="rental-form-phone" />
              </Field>
            </div>
            <Field label="Equipamento" required>
                {/* Um campo só: escolher no catálogo grava o vínculo, e digitar
                    um equipamento que a loja não vende vale pelo texto. */}
                {/* `/products` (catálogo) e não o padrão `/admin/products`: a
                    tela agora também abre no painel do vendedor, e lá o
                    endpoint administrativo responde 403 — que o Autocomplete
                    mostraria como "nada encontrado". */}
                <ProductPicker
                  path="/products"
                  // Só o que foi marcado como alugável no cadastro do produto.
                  // Antes a busca trazia o catálogo inteiro, e nada impedia
                  // abrir um contrato de locação de caixa de luva.
                  filters={{ rentable: true }}
                  productId={form.product_id || (form.equipment ? PRODUCT_FREE_TEXT : "")}
                  variantId={form.variant_id || ""}
                  selectedLabel={form.equipment}
                  onChange={({ product, variant, freeText }) => setForm({
                    ...form,
                    product_id: product?.id || null,
                    variant_id: variant?.id || null,
                    equipment: product
                      ? `${product.name}${variant && variant.label !== "Único" ? ` (${variant.label})` : ""}`
                      : (freeText ?? ""),
                  })}
                  placeholder="Busque no catálogo ou digite o equipamento"
                  allowFreeText
                  testId="rental-form-equipment"
                />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Diária (R$)" required>
                <Input type="number" step="0.01" value={form.daily_rate} onChange={set("daily_rate")} data-testid="rental-form-rate" mono />
              </Field>
              <Field label="Caução (R$)" hint="Devolvida na entrega">
                <Input type="number" step="0.01" value={form.deposit} onChange={set("deposit")} data-testid="rental-form-deposit" mono />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Entrada (R$)">
                <Input type="number" step="0.01" value={form.entry_amount} onChange={set("entry_amount")} data-testid="rental-form-entry" mono />
              </Field>
              <Field label="Parcelas">
                <Input type="number" min="1" value={form.installments} onChange={set("installments")} data-testid="rental-form-installments" mono />
              </Field>
              <Field label="Valor parcela">
                <Input type="number" step="0.01" value={form.installment_value} onChange={set("installment_value")} data-testid="rental-form-inst-value" mono />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Início" required>
                <Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="rental-form-start" />
              </Field>
              <Field label="Devolução prevista" required>
                <Input type="date" value={form.due_date} onChange={set("due_date")} data-testid="rental-form-due" />
              </Field>
            </div>
            <Button onClick={create} loading={busy} size="lg" block data-testid="rental-form-save">
              {busy ? "Criando..." : "Criar contrato"}
            </Button>
        </div>
      </Modal>
    </div>
  );
}
